import React, { useState, useEffect } from 'react';
import {
  Despesa,
  DespesaCategoria,
  FormData,
  FormDataValue,
} from './types';
import { api, API_ROUTERS } from '../../../services/api';
import { Entregador } from '../../Cadastros/Notas/types';
import RelatoriosDespesasContent from './view';

const RelatoriosDespesasContainer: React.FC = () => {
  const [despesas, setDespesas] = useState<Despesa[]>([]);
  const [categorias, setCategorias] = useState<DespesaCategoria[]>([]);
  const [entregadores, setEntregadores] = useState<Entregador[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState<FormData>({
    dataInicio: '',
    dataFim: '',
    usuarios: [],
    codigosCategoria: [],
    statuses: [],
    codigoViagem: '',
  });
  const [selectedDespesa, setSelectedDespesa] = useState<Despesa | null>(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    const fetchCategorias = async () => {
      try {
        const response = api.utils.trataResponseArray(
          await api.get(API_ROUTERS.DESPESA_CATEGORIA)
        ) as DespesaCategoria[];
        setCategorias(
          [...response].sort((a, b) => (a.ordem || 0) - (b.ordem || 0))
        );
      } catch (err) {
        console.error('Erro ao buscar categorias de despesa:', err);
      }
    };

    const fetchEntregadores = async () => {
      try {
        const response = api.utils.trataResponseArray(
          await api.get(API_ROUTERS.ENTREGADORES)
        ) as Entregador[];
        setEntregadores(response);
      } catch (err) {
        console.error('Erro ao buscar entregadores:', err);
      }
    };

    fetchCategorias();
    fetchEntregadores();
  }, []);

  const handleInputChange = (field: keyof FormData, value: FormDataValue) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }) as FormData);
  };

  const handleSearch = async () => {
    if (!formData.dataInicio || !formData.dataFim) {
      setError('Por favor, selecione as datas de início e fim');
      return;
    }

    setLoading(true);
    setError('');
    setDespesas([]);

    try {
      const params = new URLSearchParams();
      params.append('dataInicio', formData.dataInicio);
      params.append('dataFim', formData.dataFim);

      // Quando há exatamente 1 entregador, repassa pro servidor.
      // Para múltiplos, busca todos e filtra no cliente abaixo.
      if (formData.usuarios.length === 1) {
        params.append('codigoUsuario', String(formData.usuarios[0]));
      }
      if (formData.codigoViagem) {
        params.append('codigoViagem', formData.codigoViagem);
      }

      const url = `${API_ROUTERS.DESPESA}?${params.toString()}`;
      const response = await api.get(url);
      let lista = api.utils.trataResponseArray(response) as Despesa[];

      // Filtros multi aplicados no cliente (API não suporta múltiplos valores)
      if (formData.usuarios.length > 1) {
        const set = new Set(formData.usuarios.map((v) => String(v)));
        lista = lista.filter((d) => set.has(String(d.codigoUsuario)));
      }
      if (formData.codigosCategoria.length > 0) {
        lista = lista.filter((d) =>
          formData.codigosCategoria.includes(Number(d.codigoCategoria))
        );
      }
      if (formData.statuses.length > 0) {
        lista = lista.filter((d) => formData.statuses.includes(d.status));
      }

      setDespesas(lista);
    } catch (err: any) {
      console.error('Erro ao buscar despesas:', err);
      setError(
        err.response?.data?.message ||
          err.message ||
          'Erro ao buscar relatório de despesas'
      );
      setDespesas([]);
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setFormData({
      dataInicio: '',
      dataFim: '',
      usuarios: [],
      codigosCategoria: [],
      statuses: [],
      codigoViagem: '',
    });
    setDespesas([]);
    setError('');
  };

  const handleViewImage = (despesa: Despesa) => {
    setSelectedDespesa(despesa);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedDespesa(null);
  };

  return (
    <RelatoriosDespesasContent
      despesas={despesas}
      categorias={categorias}
      entregadores={entregadores}
      loading={loading}
      error={error}
      formData={formData}
      selectedDespesa={selectedDespesa}
      showModal={showModal}
      handleInputChange={handleInputChange}
      handleSearch={handleSearch}
      handleClear={handleClear}
      handleViewImage={handleViewImage}
      closeModal={closeModal}
    />
  );
};

export default RelatoriosDespesasContainer;
